import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import ConfirmModal from "./ConfirmModal";
import Input from "@/components/forms/Input.tsx";
import HolidayInclusionSelect from "@/components/forms/HolidayInclusionSelect";
import RoleDayGrid, { type RoleDayGridRow } from "@/components/sections/RoleDayGrid";
import { createRoleDayUIItem, createRoleHeaderDayUIItem } from "@/utils/roleDayGridUtils";
import type { SummaryColumn, SummaryRoleDays } from "@/equityInterfaces.ts";
import type { SummaryColumnModalProps } from "./SummaryColumnModal.types";

const DAY_IDS = ["0", "1", "2", "3", "4", "5", "6"];

function makeNewColumn(order: number): SummaryColumn {
    return {
        id: `c_${crypto.randomUUID()}`,
        label: "",
        order,
        holiday: "Inclure",
        mapOfNeedSettings: {},
    };
}

const SummaryColumnModal: React.FC<SummaryColumnModalProps> = (
    {
        open,
        onClose,
        onConfirm,
        onDelete,
        onPrefill,
        prefillReady = true,
        column,
        defaultOrder = 0,
        schedule,
    }) => {

    const [editColumn, setEditColumn] = useState<SummaryColumn>(
        column ?? makeNewColumn(defaultOrder)
    );
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setEditColumn(column ? { ...column } : makeNewColumn(defaultOrder));
            setConfirmDeleteOpen(false);
        }
    }, [open]);

    const mapOfNeedSettings = editColumn.mapOfNeedSettings || {};

    const headerDays = DAY_IDS.map(createRoleHeaderDayUIItem);
    const listOfUINeeds: RoleDayGridRow[] = (schedule?.roles ?? []).map((role) => {
        const editDaysValues = (mapOfNeedSettings[role.id] || {}).mapOfDayOptions || {};
        const days = DAY_IDS.map((dayId) => createRoleDayUIItem(dayId, editDaysValues));
        return {
            name: role.name,
            id: role.id,
            days,
            isAll: days.every((d) => d.value),
        };
    });

    const onRoleDayClick = (roleId: string, dayId: string) => {
        const newMapOfNeeds: Record<string, SummaryRoleDays> = { ...mapOfNeedSettings };
        const curNeeds = newMapOfNeeds[roleId] || { apply: false, mapOfDayOptions: {} };
        const mapOfDayOptions = { ...(curNeeds.mapOfDayOptions || {}) };
        const newMapOfDayOptions: Record<string, { apply: boolean }> = {};
        const curNeedsDay = mapOfDayOptions[dayId] || { apply: false };

        mapOfDayOptions[dayId] = { apply: !curNeedsDay.apply };

        let hasDay = false;
        for (const [key, value] of Object.entries(mapOfDayOptions)) {
            if (value && value.apply !== false) {
                newMapOfDayOptions[key] = value;
                hasDay = true;
            }
        }

        if (hasDay) {
            newMapOfNeeds[roleId] = { apply: true, mapOfDayOptions: newMapOfDayOptions };
        } else {
            delete newMapOfNeeds[roleId];
        }

        setEditColumn({ ...editColumn, mapOfNeedSettings: newMapOfNeeds });
    };

    const onRoleAllDaysClick = (roleId: string) => {
        const newMapOfNeeds: Record<string, SummaryRoleDays> = { ...mapOfNeedSettings };
        const curNeeds = newMapOfNeeds[roleId] || { apply: false, mapOfDayOptions: {} };
        const mapOfDayOptions = { ...(curNeeds.mapOfDayOptions || {}) };

        const entries = Object.entries(mapOfDayOptions);
        let willToggleAll = !entries.length;
        for (const [, value] of entries) {
            if (!(value && value.apply !== false)) {
                willToggleAll = true;
            }
        }

        if (willToggleAll) {
            const newMapOfDayOptions: Record<string, { apply: boolean }> = {};
            DAY_IDS.forEach((id) => { newMapOfDayOptions[id] = { apply: true }; });
            newMapOfNeeds[roleId] = { apply: true, mapOfDayOptions: newMapOfDayOptions };
        } else {
            delete newMapOfNeeds[roleId];
        }

        setEditColumn({ ...editColumn, mapOfNeedSettings: newMapOfNeeds });
    };

    const isNameValid = editColumn.label.trim().length > 0;
    const isPrefillEnabled = isNameValid && Object.keys(mapOfNeedSettings).length > 0 && prefillReady;
    const prefillTitle = !isNameValid
        ? "Le nom de la colonne est obligatoire"
        : !Object.keys(mapOfNeedSettings).length
            ? "Sélectionnez au moins un rôle et un jour"
            : !prefillReady
                ? "Les besoins ne sont pas encore chargés"
                : "Répartit le besoin de la colonne entre les employés éligibles. Les valeurs restent à enregistrer.";

    const handlePrefill = () => {
        if (!isPrefillEnabled || !onPrefill) {
            return;
        }
        onPrefill(editColumn);
        onClose();
    };

    const handleConfirm = () => {
        if (!isNameValid) {
            return;
        }
        onConfirm(editColumn);
        onClose();
    };

    const handleDelete = () => {
        onDelete(editColumn.id);
        onClose();
    };

    return (
        <>
        <Modal
            open={open}
            onClose={onClose}
            title={
                <Input
                    value={editColumn.label}
                    placeholder="Nom de la colonne"
                    onChange={(e) => setEditColumn({ ...editColumn, label: e.target.value })}
                    className="w-72 font-semibold"
                />
            }
            size="xl"
            footer={
                <>
                    {onPrefill && (
                        <button
                            onClick={handlePrefill}
                            disabled={!isPrefillEnabled}
                            title={prefillTitle}
                            className={`mr-auto inline-flex items-center rounded-full bg-blue-600 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isPrefillEnabled ? "cursor-pointer hover:bg-blue-700" : "opacity-50 cursor-not-allowed"}`}
                        >
                            Pré-remplir les quantités
                        </button>
                    )}
                    {column && (
                        <button
                            onClick={() => setConfirmDeleteOpen(true)}
                            className="inline-flex items-center rounded-full bg-red-600 cursor-pointer px-4 py-2 font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            Effacer
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!isNameValid}
                        title={!isNameValid ? "Le nom de la colonne est obligatoire" : undefined}
                        className={`inline-flex items-center rounded-full px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isNameValid ? "bg-green-600 cursor-pointer hover:bg-green-700" : "bg-green-600 opacity-50 cursor-not-allowed"}`}
                    >
                        Confirmer
                    </button>
                </>
            }
        >
            <div className="flex flex-col gap-4 text-gray-700">
                <RoleDayGrid
                    headerDays={headerDays}
                    listOfUINeeds={listOfUINeeds}
                    onRoleDayClick={onRoleDayClick}
                    onRoleAllDaysClick={onRoleAllDaysClick}
                />

                <label className="flex items-center gap-2">
                    <span className="text-sm font-medium">Inclusion des fériés</span>
                    <HolidayInclusionSelect
                        value={editColumn.holiday}
                        onChange={(val) => setEditColumn({ ...editColumn, holiday: val })}
                        triggerClassName="w-40"
                    />
                </label>
            </div>
        </Modal>
        <ConfirmModal
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleDelete}
            title="Supprimer la colonne"
            message={`Voulez-vous vraiment supprimer la colonne « ${editColumn.label || "Sans nom"} » ?`}
            confirmLabel="Supprimer"
            confirmVariant="danger"
        />
        </>
    );
};

export default SummaryColumnModal;
